/**
 * Tessellating design material SDF cells to polygonal meshes.
 * @module
 */
import degreeToRadian from "common/utils/degreeToRadian";
import { faceNew } from "geom/brep";
import type { PlaneBSP } from "geom/bspTypes";
import { DIST_EPSILON_32BIT, WindingDir } from "geom/core";
import { extrudeFaceAlongNormal } from "geom/extrude";
import { halfEdgeMeshFromQuadMesh3, quadMesh3FromHalfEdgeMesh, subdivideHalfEdgeMesh } from "geom/halfEdgeMesh";
import type { Mesh3, QuadMesh3 } from "geom/mesh";
import { mesh3New } from "geom/mesh";
import { planeNew } from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import { polyLine2MakeAxisAlignedRect, polyLine2MakeRoundedRect, polyLine2WindingDir } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { clipMesh3AgainstPlanes } from "geom/sutherlandHodgman";
import {
  quadMesh3ToMesh3,
  tessExtrudedRoundedRectangleAsQuads,
  tessExtrusion,
  tessRectangularPrismAsQuads,
  tessRoundedRectangularPrism,
} from "geom/tess";
import { vec2Rotate } from "geom/vec2";
import { weldMesh, weldQuadMesh } from "geom/welding";
import type { DmSlopeAxis, SdfCellInfo, SdfCommonParamsPreprocessed } from "../designMaterialTypes";
import { mergeCoplanarMeshFaces } from "./facePostProcessing";
import { clampCutXfromYForGeometry, cutXFromYForGeometry } from "./generateCellOutline";
import type { GeoBuilder } from "./geoBuilder";
import { compactGeoBuilder, geoBuilderNew } from "./geoBuilder";
import { rotateVertsX, rotateVertsY, translateVerts } from "./utils";

let cellGeometryCache: { [key: string]: GeoBuilder } = {};

function generateCellGeometryKey(
  cellInfo: Omit<SdfCellInfo, "rotation">,
  slopeAxis: DmSlopeAxis,
  useQuadMesh: boolean,
  subdivisionMaxEdgeLength: number,
  highQuality: boolean,
): string {
  return [
    cellInfo.xSize,
    cellInfo.ySize,
    cellInfo.zSize,
    slopeAxis,
    cellInfo.slope,
    cellInfo.cutXfromY,
    cellInfo.cutXfromYScale,
    cellInfo.useBevels,
    cellInfo.bevelDistanceX,
    cellInfo.bevelDistanceY,
    cellInfo.bevelDistanceZ,
    cellInfo.roundSidesMode,
    cellInfo.roundStrength,
    useQuadMesh,
    subdivisionMaxEdgeLength,
    highQuality,
  ].join(",");
}

export function fetchCellGeometry(
  cellInfo: Omit<SdfCellInfo, "rotation">,
  sdfCommon: SdfCommonParamsPreprocessed,
  useQuadMesh: boolean,
  subdivisionMaxEdgeLength: number,
  highQuality: boolean = true,
): GeoBuilder {
  // The geometry is extruded from the back plane so that the top will end up at the original zSize.
  const zOffset = cellInfo.zSize;

  const minStraightSection = Math.max(cellInfo.zSize - cellInfo.roundStrength, 0);
  if (
    (cellInfo.roundSidesMode === "roundSides" ||
      cellInfo.roundSidesMode === "roundX" ||
      cellInfo.roundSidesMode === "roundY") &&
    cellInfo.roundStrength * 2 + minStraightSection > cellInfo.zSize
  ) {
    // Make sure the extrusion is large enough to contain the rounded parts.
    cellInfo = { ...cellInfo, zSize: cellInfo.roundStrength * 2 + minStraightSection };
  }

  const slopeAxis = sdfCommon.randomTilesMode === "None" ? sdfCommon.slopeAxis : "X";
  const geoKey = generateCellGeometryKey(cellInfo, slopeAxis, useQuadMesh, subdivisionMaxEdgeLength, highQuality);
  let normalizedMesh: GeoBuilder;
  if (cellGeometryCache.hasOwnProperty(geoKey)) {
    normalizedMesh = cellGeometryCache[geoKey];
  } else {
    normalizedMesh = generateCellGeometry(cellInfo, slopeAxis, useQuadMesh, subdivisionMaxEdgeLength, highQuality);
    cellGeometryCache[geoKey] = normalizedMesh;
  }
  const copy = geoBuilderNew([...normalizedMesh.vertices], normalizedMesh.indices);
  for (let i = 2, l = copy.vertices.length; i < l; i += 3) {
    copy.vertices[i] += zOffset;
  }
  return copy;
}

export function fetchCellGeometryAsMesh(
  cellInfo: Omit<SdfCellInfo, "rotation">,
  sdfCommon: SdfCommonParamsPreprocessed,
  useQuadMesh: boolean,
  subdivisionMaxEdgeLength: number,
  highQuality: boolean = true,
): Mesh3 {
  const geo = fetchCellGeometry(cellInfo, sdfCommon, useQuadMesh, subdivisionMaxEdgeLength, highQuality);
  const copy = mesh3New(new Float32Array(geo.vertices), new Uint32Array(geo.indices));
  return copy;
}

export function clearCellGeometryCache() {
  cellGeometryCache = {};
}

/**
 * Returns vertices and indices containing the triangles making up the cell in UV space. Suitable for passing
 * directly to a THREE.BufferGeometry as position/indices.
 *
 * The cell is centered and rotation along the z axis is not applied - but slope along the X axis is.
 *
 * The top of the cell is at z = 0, and the cell extends towards negative z.
 */
function generateCellGeometry(
  cellInfo: Omit<SdfCellInfo, "rotation">,
  slopeAxis: DmSlopeAxis,
  useQuadMesh: boolean,
  subdivisionMaxEdgeLength: number,
  highQuality: boolean,
): GeoBuilder {
  const {
    xSize,
    ySize,
    zSize,
    slope,
    cutXfromY,
    cutXfromYScale,
    useBevels,
    bevelDistanceX,
    bevelDistanceY,
    bevelDistanceZ,
    roundSidesMode,
    roundStrength,
  } = cellInfo;

  const zMin = -zSize;

  const xMin = -xSize * 0.5;
  const yMin = -ySize * 0.5;

  const xMax = xSize * 0.5;
  const yMax = ySize * 0.5;

  let quadMesh: QuadMesh3 | undefined = undefined;
  let mesh: Mesh3 | undefined = undefined;
  let needsWeldVertsBeforeSubdivision = false;

  if (useQuadMesh) {
    if (roundSidesMode === "none" || roundStrength === 0) {
      quadMesh = tessRectangularPrismAsQuads(xSize, ySize, zSize);
    } else if (roundSidesMode === "roundSides") {
      let maxStepDegrees = 30;
      if (roundStrength > 0.6) {
        maxStepDegrees = 15;
      }
      if (roundStrength > 2) {
        maxStepDegrees = 10;
      }
      quadMesh = tessRoundedRectangularPrism(xSize, ySize, zSize, roundStrength, true, maxStepDegrees) as QuadMesh3;
      if (roundStrength >= Math.min(xSize * 0.5, ySize * 0.5, zSize * 0.5)) {
        needsWeldVertsBeforeSubdivision = true;
      }
      if (quadMesh === undefined) {
        return geoBuilderNew();
      }
      if (cutXfromY && cutXfromYScale > 0.0) {
        const verts = quadMesh.verts;
        const clampedCutXfromYScale = clampCutXfromYForGeometry(cellInfo);
        if (clampedCutXfromYScale > 0.0) {
          for (let i = 0, l = verts.length; i < l; i += 3) {
            verts[i] = cutXFromYForGeometry(verts[i], verts[i + 1], clampedCutXfromYScale);
          }
        }
      }
    } else if (roundSidesMode === "roundZ") {
      quadMesh = tessExtrudedRoundedRectangleAsQuads(xSize, ySize, zSize, roundStrength, 10);
      if (quadMesh === undefined) {
        return geoBuilderNew();
      }
    } else if (roundSidesMode === "roundX") {
      quadMesh = tessExtrudedRoundedRectangleAsQuads(zSize, ySize, xSize, roundStrength, 10);
      if (quadMesh === undefined) {
        return geoBuilderNew();
      }
      rotateVertsY(quadMesh.verts, 90);
    } else {
      if (!(roundSidesMode === "roundY")) {
        console.assert(false);
      }
      quadMesh = tessExtrudedRoundedRectangleAsQuads(xSize, zSize, ySize, roundStrength, 10);
      if (quadMesh === undefined) {
        return geoBuilderNew();
      }
      rotateVertsX(quadMesh.verts, 90);
    }
    translateVerts(quadMesh.verts, 0, 0, zMin + zSize * 0.5);
  } else {
    if (roundSidesMode === "roundSides") {
      let maxStepDegrees = 30;
      if (roundStrength > 0.6) {
        maxStepDegrees = 15;
      }
      if (roundStrength > 2) {
        maxStepDegrees = 10;
      }
      mesh = tessRoundedRectangularPrism(xSize, ySize, zSize, roundStrength, false, maxStepDegrees);
      if (mesh === undefined) {
        return geoBuilderNew();
      }
      if (cutXfromY && cutXfromYScale > 0.0) {
        const verts = mesh.verts;
        const clampedCutXfromYScale = clampCutXfromYForGeometry(cellInfo);
        if (clampedCutXfromYScale > 0.0) {
          for (let i = 0, l = verts.length; i < l; i += 3) {
            verts[i] = cutXFromYForGeometry(verts[i], verts[i + 1], clampedCutXfromYScale);
          }
        }
      }
      translateVerts(mesh.verts, 0, 0, zMin + zSize * 0.5);
    } else {
      let extrusionDistance, plane, uSize, vSize;
      if (roundSidesMode === "none") {
        extrusionDistance = zSize;
        uSize = xSize * 0.5;
        vSize = ySize * 0.5;
        plane = planeNew(0, 0, zMin, 1, 0, 0, 0, 1, 0);
      } else if (roundSidesMode === "roundZ") {
        extrusionDistance = zSize;
        uSize = xSize * 0.5;
        vSize = ySize * 0.5;
        plane = planeNew(0, 0, zMin, 1, 0, 0, 0, 1, 0);
      } else if (roundSidesMode === "roundX") {
        extrusionDistance = xSize;
        uSize = ySize * 0.5;
        vSize = zSize * 0.5;
        plane = planeNew(xMin, 0, zMin + zSize * 0.5, 0, 1, 0, 0, 0, 1);
      } else if (roundSidesMode === "roundY") {
        extrusionDistance = ySize;
        uSize = xSize * 0.5;
        vSize = zSize * 0.5;
        plane = planeNew(0, yMin, zMin + zSize * 0.5, 1, 0, 0, 0, 0, -1);
      }
      let loop: PolyLine2 | undefined;
      if (roundStrength === 0 || roundSidesMode === "none") {
        // The order of the negative and positive arguments here is important to set the winding direction.
        loop = polyLine2MakeAxisAlignedRect(uSize, -vSize, -uSize, vSize);
        if (roundSidesMode === "none" && cutXfromY && cutXfromYScale > 0.0) {
          for (let i = 0, l = loop.length; i < l; i += 2) {
            loop[i] = cutXFromYForGeometry(loop[i], loop[i + 1], cutXfromYScale);
          }
        }
      } else {
        // Generate a rounded rectangle for extruding. The mesh cannot be trapezoidal in this case (cutXFromY is ignored).
        loop = polyLine2MakeRoundedRect(uSize, -vSize, -uSize, vSize, roundStrength);
        if (loop === undefined) {
          return geoBuilderNew();
        }
      }
      if (!(polyLine2WindingDir(loop) === WindingDir.CW)) {
        console.assert(false, "polyline used as the outer loop of the face should be CW");
      }
      const face = faceNew([loop], plane);
      const extrusion = extrudeFaceAlongNormal(face, extrusionDistance);
      mesh = tessExtrusion(extrusion);
    }
  }

  if (useQuadMesh) {
    if (quadMesh === undefined) {
      return geoBuilderNew();
    }
    if (subdivisionMaxEdgeLength < Infinity) {
      if (needsWeldVertsBeforeSubdivision) {
        // The subdivision relies on that there are no duplicate vertices.
        weldQuadMesh(quadMesh, DIST_EPSILON_32BIT, true);
      }
      const halfEdgeMesh = halfEdgeMeshFromQuadMesh3(quadMesh);
      subdivideHalfEdgeMesh(halfEdgeMesh, subdivisionMaxEdgeLength);
      quadMesh = quadMesh3FromHalfEdgeMesh(halfEdgeMesh);
    }

    mesh = quadMesh3ToMesh3(quadMesh, false);
  }

  if (mesh === undefined) {
    return geoBuilderNew();
  }

  if (useBevels) {
    const planeEquations: PlaneBSP[] = [];

    // Bevel Z
    if (bevelDistanceZ > 0) {
      const bevelPlane = planeNew(xMin + Math.SQRT2 * bevelDistanceZ, yMin, 0, Math.SQRT1_2, -Math.SQRT1_2, 0, 0, 0, 1);
      planeEquations.push(planeBSPFromPlane(bevelPlane));

      bevelPlane.originY = yMax;
      bevelPlane.uX = -Math.SQRT1_2;
      planeEquations.push(planeBSPFromPlane(bevelPlane));

      bevelPlane.originX = xMax - Math.SQRT2 * bevelDistanceZ;
      bevelPlane.uY = Math.SQRT1_2;
      planeEquations.push(planeBSPFromPlane(bevelPlane));

      bevelPlane.originY = yMin;
      bevelPlane.uX = Math.SQRT1_2;
      planeEquations.push(planeBSPFromPlane(bevelPlane));
    }

    // X and Y bevels are only applied to the top of the shape.

    // Bevel X
    if (bevelDistanceX > 0) {
      const bevelPlane = planeNew(
        xMin,
        yMin + Math.SQRT2 * bevelDistanceX,
        0,
        0,
        -Math.SQRT1_2,
        -Math.SQRT1_2,
        1,
        0,
        0,
      );
      planeEquations.push(planeBSPFromPlane(bevelPlane));

      bevelPlane.originY = yMax - Math.SQRT2 * bevelDistanceX;
      bevelPlane.uZ = Math.SQRT1_2;
      planeEquations.push(planeBSPFromPlane(bevelPlane));
    }

    // Bevel Y
    if (bevelDistanceY > 0) {
      const bevelPlane = planeNew(
        xMin + Math.SQRT2 * bevelDistanceY,
        yMin,
        0,
        -Math.SQRT1_2,
        0,
        -Math.SQRT1_2,
        0,
        -1,
        0,
      );
      planeEquations.push(planeBSPFromPlane(bevelPlane));

      bevelPlane.originX = xMax - Math.SQRT2 * bevelDistanceY;
      bevelPlane.uZ = Math.SQRT1_2;
      planeEquations.push(planeBSPFromPlane(bevelPlane));
    }

    if (planeEquations.length > 0) {
      mesh = clipMesh3AgainstPlanes(mesh, planeEquations, true);
    }
  }

  const vertices = mesh.verts;
  // Apply slope
  if (slope !== 0.0 && slopeAxis === "X") {
    const slopeRad = degreeToRadian(slope);
    for (let i = 0, ic = vertices.length / 3; i < ic; i++) {
      const offset = slopeRad > 0 ? yMax : yMin;
      const [ny, nz] = vec2Rotate(vertices[i * 3 + 1] - offset, vertices[i * 3 + 2], slopeRad);
      vertices[i * 3 + 1] = ny + offset;
      vertices[i * 3 + 2] = nz;
    }
  }

  if (!highQuality) {
    return geoBuilderNew(mesh.verts, mesh.tris);
  }

  if (subdivisionMaxEdgeLength === Infinity) {
    mesh = mergeCoplanarMeshFaces({ mesh });
  }

  weldMesh(mesh, DIST_EPSILON_32BIT, true);

  const geo = geoBuilderNew(mesh.verts, mesh.tris);
  compactGeoBuilder(geo);
  return geo;
}
